import { getStyle } from './getStyle';
import { getVSMEText } from '../../../../main/functions/vsme/getVSMEText';
import { IDs } from '../../../../helpers/IDs';
import * as React from 'react';
import { Table } from './Table';
import { WpdfText } from '../../pdf/WpdfText.';
import { WpdfView } from '../../pdf/WpdfView';

export interface ModulPatProps {
    data: any;
    isPdf?: boolean;
    jahr: number;
    standortTid?: number;
    getPageNum?: (key: string, page: number, level: number) => void;
}

export class ModulPat extends React.Component<ModulPatProps, {}> {

    constructor(props: any) {
        super(props);
        this.state = {
        };
        this.init();
    }

    private isAvailable: boolean;
    private Table: any;

    componentDidUpdate(prevProps: any): void {
        if (this.props.standortTid !== prevProps.standortTid || this.props.jahr !== prevProps.jahr) {
            this.init();
        }
    }

    init(): void {
        const b1: any = ((this.props.data || {})[IDs.ENT_VSME_B1] || [])
            .find((item: any) => (item.standort_tid_orig || item.standort_tid) === this.props.standortTid) || {};
        this.isAvailable = Object.keys(b1).length ? (b1.modul === 'basis_pat' || b1.modul === 'basis_pat_geschaeft') : true;
        this.Table = new Table(this.props.data, { jahr: this.props.jahr, standort_tid: this.props.standortTid });
    }

    render(): JSX.Element {
        if (!this.isAvailable) {
            return null;
        }
        const { isPdf } = this.props;

        return (
            <WpdfView pdf={isPdf} break>
                {this.renderHeading('II) PAT-Modul', 1)}
                {this.renderN1()}
                {this.renderN2()}
                {this.renderN3()}
                {this.renderN4()}
                {this.renderN5()}
            </WpdfView>
        );
    }

    renderAbsatz(keys: string[]): JSX.Element {
        const { data, isPdf, jahr, standortTid } = this.props;

        return (
            <WpdfView style={getStyle('div')} pdf={isPdf}>
                <WpdfText pdf={isPdf}>
                    {(keys || []).map((key: string) => getVSMEText(key, data, jahr, standortTid) || '').join(' ')}
                </WpdfText>
            </WpdfView>
        );
    }

    renderElement(key: string, item: any, names: any, styles: any): JSX.Element {
        const { data, isPdf, jahr, standortTid } = this.props;
        styles = styles || {};
        const style = (key: string) => {
            let css: any = {};

            (styles[key] || []).forEach((prop: string) => css = { ...css, ...(getStyle(prop, isPdf) || {}) });

            return css;
        };

        return (
            <WpdfView key={`element-${key}`} style={{ ...style('element'), display: 'flex', flexDirection: 'column' }} pdf={isPdf}>
                <WpdfText style={style('name')} pdf={isPdf}>
                    {getVSMEText(names[key], data, jahr, standortTid) || ''}
                </WpdfText>
                <WpdfText style={{ ...style('text'), whiteSpace: 'pre-line' }} pdf={isPdf}>
                    {item[key] || ''}
                </WpdfText>
            </WpdfView>
        );
    }

    renderElements(entity: string, names: any, styles: any): JSX.Element[] {
        const elements: JSX.Element[] = [];
        const Table = this.Table;
        Table.processData((entity), null, (item: any) => {
            Object.keys(names).forEach((key: string) => {
                if (typeof item[key] === 'undefined') {
                    return;
                }
                elements.push(this.renderElement(key, item, names, styles));
            });
        });

        return elements;
    }

    renderHeading(name: string, level: number): JSX.Element {
        const { getPageNum, isPdf } = this.props;
        const style: React.CSSProperties = level === 1
            ? { ...getStyle('h1'), ...(isPdf && { marginTop: 0 }) }
            : (level === 2 ? getStyle('h2') : {});

        return (
            <WpdfView style={style} pdf={isPdf}>
                <WpdfText
                    pdf={isPdf}
                    getPageNum={(pageNumber: number) => typeof getPageNum === 'function' && getPageNum(name, pageNumber, level)}
                >
                    {name || ''}
                </WpdfText>
            </WpdfView>
        );
    }

    renderN1(): JSX.Element {
        const names: any = {
            gruppen_produkte_dienstleistungen: 'n1_absatz_a', unternehmensmaerkte: 'n1_absatz_b', geschaeftsbeziehungen: 'n1_absatz_c',
            strategieelemente_nachhaltigkeit: 'n1_absatz_d'
        };
        const styles: any = { element: ['list'], name: ['div', 'bold'], text: ['div', 'list'] };

        return <>
            {this.renderHeading('N1: Strategie – Geschäftsmodell', 2)}
            {this.renderElements(IDs.ENT_VSME_N1, names, styles)}
        </>;
    }

    renderN2(): JSX.Element {
        const names: any = {
            auswirkungen_menschen_umwelt: 'n2_absatz_a', auswirkungen_finanz_ertragslage: 'n2_absatz_b',
            auswirkungen_taetigkeiten_strategie: 'n2_absatz_c'
        };
        const styles: any = { element: ['list'], name: ['div', 'bold'], text: ['div', 'list'] };

        return <>
            {this.renderHeading('N2: Wesentliche Aspekte der Nachhaltigkeit', 2)}
            {this.renderAbsatz(['n2_absatz1'])}
            {this.renderElements(IDs.ENT_VSME_N2, names, styles)}
        </>;
    }

    renderN3(): JSX.Element {
        const names: any = { info_strategien: 'n3_absatz2', info_massnahmen: 'n3_absatz3' };
        const styles: any = { name: ['div', 'bold'], text: ['div'] };

        return <>
            {this.renderHeading('N3: Management von Nachhaltigkeitsfragen', 2)}
            {this.renderAbsatz(['n3_absatz1'])}
            {this.renderElements(IDs.ENT_VSME_N3, names, styles)}
        </>;
    }

    renderN4(): JSX.Element {
        const names: any = { beteiligungskategorien: 'n4_absatz2', engagement_aktivitaeten: 'n4_absatz3' };
        const styles: any = { name: ['div', 'bold'], text: ['div'] };

        return <>
            {this.renderHeading('N4: Wichtige Interessensgruppen', 2)}
            {this.renderAbsatz(['n4_absatz1'])}
            {this.renderElements(IDs.ENT_VSME_N4, names, styles)}
        </>;
    }

    renderN5(): JSX.Element {
        const names: any = { nachhaltigkeitszustaendigkeiten: 'n5_absatz1' };
        const styles: any = { name: ['div'], text: ['div'] };

        return <>
            {this.renderHeading('N5: Nachhaltige Unternehmensführung', 2)}
            {this.renderElements(IDs.ENT_VSME_N5, names, styles)}
        </>;
    }
}
